import type { ComponentType } from "react";

import type { CircleAIShellStore } from "./circleAIShellStore";

export interface ConversationPanelSurfaceSlice {
  activeConversationPanelSurface: string | null;
  closeConversationPanelSurface: () => void;
  conversationPanelSurfaces: Record<string, ComponentType>;
  conversationPanelSurfaceTrigger: HTMLElement | null;
  openConversationPanelSurface: (id: string) => void;
  registerConversationPanelSurface: (
    id: string,
    surface: ComponentType,
  ) => void;
  unregisterConversationPanelSurface: (id: string) => void;
}

type SetShellState = (
  partial:
    | Partial<CircleAIShellStore>
    | ((state: CircleAIShellStore) => Partial<CircleAIShellStore>),
) => void;

type SurfaceRegistryState = Pick<
  ConversationPanelSurfaceSlice,
  "activeConversationPanelSurface" | "conversationPanelSurfaces"
>;

export const selectActiveConversationPanelSurface = (
  s: SurfaceRegistryState,
): ComponentType | null =>
  s.activeConversationPanelSurface === null
    ? null
    : (s.conversationPanelSurfaces[s.activeConversationPanelSurface] ?? null);

export const selectHasActiveConversationPanelSurface = (
  s: SurfaceRegistryState,
) => selectActiveConversationPanelSurface(s) !== null;

export const selectIsConversationPanelSurfaceActive = (
  s: SurfaceRegistryState,
  id: string,
) =>
  s.activeConversationPanelSurface === id &&
  selectActiveConversationPanelSurface(s) !== null;

export const createConversationPanelSurfaceSlice = (
  set: SetShellState,
): ConversationPanelSurfaceSlice => ({
  activeConversationPanelSurface: null,
  conversationPanelSurfaces: {},
  conversationPanelSurfaceTrigger: null,
  registerConversationPanelSurface: (id, surface) =>
    set(s => ({
      conversationPanelSurfaces: {
        ...s.conversationPanelSurfaces,
        [id]: surface,
      },
    })),
  unregisterConversationPanelSurface: id =>
    set(s => ({
      conversationPanelSurfaces: Object.fromEntries(
        Object.entries(s.conversationPanelSurfaces).filter(
          ([key]) => key !== id,
        ),
      ),
      activeConversationPanelSurface:
        s.activeConversationPanelSurface === id
          ? null
          : s.activeConversationPanelSurface,
    })),
  openConversationPanelSurface: id =>
    set(s =>
      s.conversationPanelSurfaces[id] === undefined
        ? {}
        : {
            activeConversationPanelSurface: id,
            conversationPanelSurfaceTrigger:
              typeof document !== "undefined" &&
              document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null,
            conversationPanelState: "default",
            isAdminDockForced: true,
          },
    ),
  closeConversationPanelSurface: () =>
    set({ activeConversationPanelSurface: null }),
});
